if (!customElements.get('toolbar-mobile')) {
  class Toolbarmobile extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.mobile = matchMedia('(max-width: 1024px)');
      this.measure = theme.utils.rafThrottle(() => this.setHeight());
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this);
      this.mobile.addEventListener('change', this.measure, options);
      window.addEventListener('resize', this.measure, options);
      window.visualViewport?.addEventListener('resize', this.measure, options);
      document.addEventListener('focusin', this.measure, options);
      document.addEventListener('focusout', this.measure, options);
      document.addEventListener('shopify:section:load', () => { this.bindHeader(); this.setHeight(); }, options);
      this.addEventListener('pointerdown', event => {
        const item = event.target.closest('.toolbar__item');
        if (item && this.contains(item) && item.getAttribute('aria-disabled') !== 'true') this.updateIndicator(item);
      }, options);
      this.addEventListener('pointercancel', this.measure, options);
      this.addEventListener('click', this.measure, options);

      // Reflect real drawer state rather than highlighting a click that failed to open.
      this.drawers = [...this.querySelectorAll('[data-side-drawer]')].map(opener => ({
        opener,
        drawer: document.querySelector(opener.dataset.sideDrawer),
        button: opener.querySelector('button'),
      }));
      this.drawerObserver = new MutationObserver(() => this.syncDrawers());
      this.drawers.forEach(({ drawer }) => {
        if (drawer) this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      });
      this.syncDrawers();

      // ELLA updates these existing count nodes after cart/wishlist changes.
      this.countObserver = new MutationObserver(() => this.syncCounts());
      this.countObserver.observe(this, { childList: true, characterData: true, subtree: true });
      this.syncCounts();
      this.bindHeader();
      this.setHeight();
    }

    bindHeader() {
      const header = document.querySelector('#header-component.adora-navigation');
      if (this.header === header) return;
      this.releaseHeader();
      // Shopify can connect the replacement section before disconnecting the old one.
      if (header?.mobileToolbar && header.mobileToolbar !== this) header.mobileToolbar.releaseHeader();
      this.header = header;
      this.menuButton = this.querySelector('[data-toolbar-menu]');
      this.homeLink = this.querySelector('[data-toolbar-home-link]');
      this.homeItem = this.querySelector('[data-toolbar-home-item]');
      this.navigation = header?.querySelector('adora-mobile-navigation');
      if (!header || !this.navigation || !this.menuButton) return;
      this.headerInert = header.inert;
      this.headerAriaHidden = header.getAttribute('aria-hidden');
      header.mobileToolbar = this;
      this.navigation.connectToolbar?.(this);
      this.menuButton.setAttribute('aria-controls', this.navigation.panel?.id || '');
      this.headerBindingController = new AbortController();
      this.menuButton.addEventListener('click', () => {
        if (this.navigation.details.open) this.navigation.close(true);
        else this.navigation.openFromToolbar(this);
      }, { signal: this.headerBindingController.signal });
      // Reuse ELLA's scroll frame. Only a non-sticky header needs a fallback listener.
      if (!header.onScroll) {
        this.flowAnchor = document.createElement('div');
        this.flowAnchor.setAttribute('aria-hidden', 'true');
        this.flowAnchor.style.cssText = 'display:block;height:0;pointer-events:none';
        header.parentElement.before(this.flowAnchor);
        this.fallbackController = new AbortController();
        document.addEventListener('scroll', this.measure, { passive: true, signal: this.fallbackController.signal });
      }
    }

    syncNavigation(scrollTop = Math.max(0, document.scrollingElement?.scrollTop || 0)) {
      const managed = Boolean(this.header && this.navigation && this.menuButton && this.mobile.matches);
      if (!managed) {
        this.setPresentation(null);
        return false;
      }
      const anchor = this.header.stickyAnchor || this.flowAnchor;
      const origin = (anchor?.getBoundingClientRect().top || 0) + scrollTop;
      const threshold = Math.max(96, origin + this.header.offsetHeight + 24);
      const menuOpen = this.navigation.details.open;
      const drawerOpen = this.drawers.some(({ drawer }) => drawer?.classList.contains('open') || drawer?.classList.contains('active'));
      const keyboardFocus = (this.header.contains(document.activeElement) || this.contains(document.activeElement)) && document.activeElement?.matches(':focus-visible');
      let bottom = this.collapsed === undefined ? scrollTop >= threshold : this.collapsed;
      if (!menuOpen && !drawerOpen && !this.hasAttribute('data-keyboard-open') && !keyboardFocus) {
        bottom = this.collapsed ? scrollTop > threshold - 32 : scrollTop >= threshold;
      }
      this.setPresentation(bottom);
      // Keep the source row pinned until the handoff finishes; its height never changes.
      this.header.dataset.stickyState = scrollTop > origin ? 'active' : 'inactive';
      this.header.dataset.scrollDirection = 'none';
      return true;
    }

    setPresentation(bottom) {
      const adaptive = bottom !== null;
      const state = adaptive ? (bottom ? 'bottom' : 'top') : 'standalone';
      if (this.getAttribute('data-navigation-state') === state) return;
      this.collapsed = adaptive ? bottom : undefined;
      this.setAttribute('data-navigation-state', state);
      this.inert = adaptive && !bottom;
      this.setAttribute('aria-hidden', String(this.inert));
      if (this.header) {
        if (adaptive) {
          this.header.dataset.mobileNavigation = state;
          this.header.inert = bottom;
          this.header.setAttribute('aria-hidden', String(bottom));
        } else {
          delete this.header.dataset.mobileNavigation;
          this.header.inert = this.headerInert || false;
          if (this.headerAriaHidden == null) this.header.removeAttribute('aria-hidden');
          else this.header.setAttribute('aria-hidden', this.headerAriaHidden);
        }
      }
      if (this.menuButton) this.menuButton.hidden = !adaptive;
      if (this.homeLink) this.homeLink.hidden = adaptive;
      if (this.homeItem) this.homeItem.toggleAttribute('data-current-page', !adaptive && this.homeLink?.hasAttribute('aria-current'));
      this.updateHeight();
    }

    syncMenu() {
      const open = Boolean(this.collapsed && this.navigation?.details.open);
      this.toggleAttribute('data-menu-open', open);
      this.menuButton?.setAttribute('aria-expanded', String(open));
      this.homeItem?.classList.toggle('is-active', open);
      this.updateIndicator();
    }

    releaseHeader() {
      this.navigation?.close();
      this.navigation?.disconnectToolbar(this);
      this.headerBindingController?.abort();
      this.setPresentation(null);
      if (this.header?.mobileToolbar === this) this.header.mobileToolbar = null;
      this.fallbackController?.abort();
      this.flowAnchor?.remove();
      this.flowAnchor = null;
      this.header = null;
      this.navigation = null;
    }

    syncDrawers() {
      this.drawers.forEach(({ opener, drawer, button }) => {
        const open = drawer?.classList.contains('open') || drawer?.classList.contains('active') || false;
        opener.classList.toggle('is-active', open);
        button?.setAttribute('aria-expanded', String(open));
      });
      this.updateIndicator();
    }

    updateIndicator(preview) {
      const item = preview || this.querySelector('.toolbar__item.is-active') || this.querySelector('.toolbar__item[data-current-page]');
      const visible = Boolean(item && this.mobile.matches);
      this.style.setProperty('--toolbar-active-opacity', visible ? '1' : '0');
      if (!visible) return;
      this.style.setProperty('--toolbar-active-x', `${item.offsetLeft}px`);
      this.style.setProperty('--toolbar-active-width', `${item.offsetWidth}px`);
      this.toggleAttribute('data-indicator-ready', true);
    }

    syncCounts() {
      this.querySelectorAll('[data-cart-count], [data-wishlist-count]').forEach(count => {
        const empty = !count.textContent.trim() || count.textContent.trim() === '0';
        count.closest('.cart-count-bubble, .wishlist-count-bubble')?.toggleAttribute('data-empty', empty);
      });
    }

    setHeight() {
      const viewport = window.visualViewport;
      const editing = document.activeElement?.matches('input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]');
      const keyboardOpen = Boolean(editing && viewport && window.innerHeight - viewport.height > 150);
      this.toggleAttribute('data-keyboard-open', keyboardOpen);
      this.syncNavigation();
      this.updateHeight();
      this.updateIndicator();
    }

    updateHeight() {
      // Include the floating gap/safe area so footer links and Sticky ATC stay clear.
      const bottom = this.mobile.matches ? parseFloat(getComputedStyle(this).bottom) || 0 : 0;
      const visible = this.mobile.matches && this.getAttribute('data-navigation-state') !== 'top';
      const height = visible ? Math.ceil(this.getBoundingClientRect().height + bottom) : 0;
      document.body.style.setProperty('--toolbar-mobile-height', `${height}px`);
    }

    disconnectedCallback() {
      this.releaseHeader();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.countObserver?.disconnect();
      this.measure?.cancel();
      if (!document.querySelector('toolbar-mobile.adora-toolbar')) {
        document.body.style.setProperty('--toolbar-mobile-height', '0px');
      }
    }
  }

  customElements.define('toolbar-mobile', Toolbarmobile);
}
